import { useEffect, useRef, useState } from "react";
import { ProductObj, VariantsObj } from "../Types";
import { AvailableStandardTemplates, BundleTypes, getPixelsForImage, updateShowImage } from "../constants";
import { InspectorEditableBlock } from "../CommonFields/InspectorEditableBlock";
import { NAGAIA } from "../../constants";
import { getModifiedImageUrl } from "../../utilities";
import styles from "./BundlePreviewWrapper.module.css";

const ProductImageWrapper = ({
    product,
    handleProductClick,
    bundleConfig = {},
    isMobile = false,
    selectedVariants,
    overrideHeight = null,
    showHoverIcon = false,
    hoverIconType = "searchIcon",
    renderQuantitySelector,
    overrideQtyVisibility = false,
}: {
    product: ProductObj;
    handleProductClick?: (e: any) => void;
    bundleConfig: any;
    isMobile: any;
    selectedVariants: VariantsObj[];
    overrideHeight?: number | null;
    showHoverIcon?: boolean;
    hoverIconType?: "button" | "searchIcon";
    renderQuantitySelector?: any;
    overrideQtyVisibility?: boolean;
}) => {
    const shouldForceFirstImage = ["xc4r50-fx.myshopify.com", "testbundle22.myshopify.com"].includes(
        window?.shopify?.config?.shop || window?.kefi_bundles?.shop,
    );
    const isFirstVariantChange = useRef(shouldForceFirstImage);
    const [showImage, setShowImage] = useState(false);
    const [img, setImg] = useState({ altText: "", originalSrc: "" });
    useEffect(() => {
        getVariantImage(selectedVariants);
    }, [selectedVariants]);

    const getVariantImage = (sv: any) => {
        if (sv.length >= 1) {
            if (isFirstVariantChange.current) {
                isFirstVariantChange.current = false;
                setImg(product?.images?.[0]);
                return;
            }
            setImg(sv[0]?.image?.originalSrc ? sv[0]?.image : product?.images?.[0]);
        } else {
            setImg(product?.images?.[0]);
        }
    };

    // Inside useEffect
    useEffect(() => {
        setShowImage(updateShowImage(bundleConfig?.bundles_design?.product_img_show, isMobile));
    }, [bundleConfig?.bundles_design?.product_img_show, isMobile]);

    const [isHovered, setIsHovered] = useState(false);

    const renderHoverIcon = () => {
        if (!showHoverIcon || !isHovered) return null;

        switch (hoverIconType) {
            case "button":
                return (
                    <div
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            backgroundColor: "rgba(0, 0, 0, 0.1)",
                            color: "#fff",
                            borderRadius: `${bundleConfig?.bundles_design?.product_img_border_radius}px`,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 2,
                            flexDirection: "column",
                        }}
                    >
                        {product.variants.length > 1 ? (
                            <button
                                style={{
                                    marginTop: "auto",
                                    marginBottom: "10px",
                                    backgroundColor: "#fff",
                                    padding: "8px",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                    width: "80%",
                                    border: `1px solid ${bundleConfig?.bundles_design?.add_to_cart_bg_color}`,
                                    background: bundleConfig?.bundles_design?.add_to_cart_bg_color,
                                    color: bundleConfig?.bundles_design?.add_to_cart_text_color,
                                    fontSize: bundleConfig?.bundles_design?.add_to_cart_text_size,
                                    fontWeight: bundleConfig?.bundles_design?.add_to_cart_text_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design?.add_to_cart_text_style?.split(" ")[1],
                                    wordBreak: "break-all",
                                }}
                            >
                                {bundleConfig?.choose_option_text || "Choose Option"}
                            </button>
                        ) : (
                            <div
                                style={{
                                    marginTop: "auto",
                                    marginBottom: "10px",
                                    width: "80%",
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {renderQuantitySelector}
                            </div>
                        )}
                    </div>
                );

            case "searchIcon":
            default:
                return (
                    <div
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            backgroundColor: "rgba(0, 0, 0, 0.2)",
                            color: "#fff",
                            borderRadius: bundleConfig?.bundles_design?.product_img_border_radius + "px",
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 2,
                        }}
                    >
                        <svg
                            style={{
                                width: "auto",
                                height: "auto",
                            }}
                            width="30"
                            height="30"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 60 60"
                        >
                            <path
                                fillRule="evenodd"
                                d="M41.87 24a17.87 17.87 0 11-35.74 0 17.87 17.87 0 0135.74 0zm-3.15 18.96a24 24 0 114.24-4.24L59.04 54.8a3 3 0 11-4.24 4.24L38.72 42.96z"
                                fill="#ffffff"
                            />
                        </svg>
                    </div>
                );
        }
    };
    const isBBType = [BundleTypes.BUNDLE_BUILDER, BundleTypes.BB_PLACEHOLDER].includes(bundleConfig?.bundle_type);
    const isAutoHeight = isBBType && bundleConfig.bundles_design?.is_product_img_height_auto;

    const { bundle_type, original_bundle_type } = bundleConfig || {};
    const quantity = overrideQtyVisibility ? product?.quantity : product?.qty;
    const isExcludedType =
        [BundleTypes.FBT, BundleTypes.BUNDLE_BUILDER, BundleTypes.BB_PLACEHOLDER].includes(bundle_type) ||
        (original_bundle_type &&
            [BundleTypes.BUNDLE_BUILDER, BundleTypes.BB_PLACEHOLDER].includes(original_bundle_type));
    // Show badge only if qty > 1
    const shouldShowBadge = quantity > 1 && (overrideQtyVisibility || !isExcludedType);
    return !showImage ? (
        <></>
    ) : (
        <InspectorEditableBlock bundleType={bundleConfig?.bundle_type} settingKey="productImage">
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    border: [BundleTypes.BUNDLE_BUILDER, BundleTypes.BB_PLACEHOLDER]?.includes(
                        bundleConfig?.bundle_type,
                    )
                        ? "none !important"
                        : " 1px solid #ccc",
                    borderWidth: bundleConfig.bundles_design?.product_img_border_width,
                    borderColor: bundleConfig.bundles_design?.product_img_border_color,
                    borderRadius: bundleConfig.bundles_design?.product_img_border_radius + "px",
                    cursor: "pointer",
                    width: [BundleTypes.BUNDLE_BUILDER, BundleTypes.BB_PLACEHOLDER]?.includes(bundleConfig?.bundle_type)
                        ? "100%"
                        : getPixelsForImage(bundleConfig.bundles_design?.product_img_size),
                    height: isAutoHeight
                        ? "auto"
                        : overrideHeight != null && overrideHeight >= 40
                        ? overrideHeight
                        : getPixelsForImage(bundleConfig.bundles_design?.product_img_size),
                    maxHeight: isAutoHeight ? (isMobile ? "250px" : "350px") : undefined,
                    minWidth: [BundleTypes.BUNDLE_BUILDER, BundleTypes.BB_PLACEHOLDER]?.includes(
                        bundleConfig?.bundle_type,
                    )
                        ? "100%"
                        : getPixelsForImage(bundleConfig.bundles_design?.product_img_size),
                    minHeight: isAutoHeight
                        ? "auto"
                        : overrideHeight != null && overrideHeight >= 40
                        ? overrideHeight
                        : getPixelsForImage(bundleConfig.bundles_design?.product_img_size),
                    position: "relative",
                }}
                className={styles.productCardImageWrapper}
                onClick={handleProductClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <img
                    alt={img?.altText}
                    src={
                        [NAGAIA].includes(window?.shopify?.config?.shop || window?.kefi_bundles?.shop) &&
                        bundleConfig?.bundle_type == BundleTypes.BB_PLACEHOLDER
                            ? getModifiedImageUrl(img?.originalSrc, "150", "150")
                            : bundleConfig?.bundle_type == BundleTypes.BUNDLE_BUILDER &&
                              ["by-and-by-n7mzgwwg.myshopify.com"].includes(
                                  window?.shopify?.config?.shop || window?.kefi_bundles?.shop,
                              )
                            ? getModifiedImageUrl(img?.originalSrc, "1000", "1000")
                            : getModifiedImageUrl(img?.originalSrc, "300", "300")
                    }
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        borderRadius: bundleConfig.bundles_design?.product_img_border_radius + "px",
                        filter: showHoverIcon && isHovered ? "contrast(0.7)" : "",
                    }}
                    className={styles.productCardImage}
                />

                {renderHoverIcon()}

                {shouldShowBadge && (
                    <div className={styles.productCardImageWrapperQtyBadge}>
                        x
                        {overrideQtyVisibility
                            ? product?.quantity || 0
                            : // @ts-expect-error hack
                              product?.qty || 0}
                    </div>
                )}
            </div>
        </InspectorEditableBlock>
    );
};

export default ProductImageWrapper;
